import { useFragment } from '@frameio/graphql-client';
import type {
  CompleteAccountUserFragment,
  Membership,
  ResourceMembershipFragment,
} from '@frameio/graphql-client/generated';
import {
  CompleteAccountUser,
  ResourceMembership,
} from '@frameio/graphql-client/generated';
import { intl } from '@frameio/i18n';
import { toSupportedLocaleOrDefault } from '@frameio/shared/utils/toSupportedLocale';

/**
 * A reactive hook for reading a membership fragment that will update in response to cache changes.
 * Passes locale so Apollo can resolve parameterized Role fields (name, description) from cache.
 */
export function useReadMembershipFragment(id: Membership['id']) {
  const locale = toSupportedLocaleOrDefault(intl().locale);

  const membershipFragment = useFragment<ResourceMembershipFragment>({
    fragment: ResourceMembership,
    fragmentName: 'ResourceMembership',
    from: {
      __typename: 'Membership',
      id,
    },
    variables: { locale },
  });

  const accountUserFragment = useFragment<CompleteAccountUserFragment>({
    fragment: CompleteAccountUser,
    fragmentName: 'CompleteAccountUser',
    from: {
      __typename: 'AccountUser',
      id,
    },
  });

  return {
    member: membershipFragment.data as ResourceMembershipFragment,
    accountUser: accountUserFragment.data as CompleteAccountUserFragment,
  };
}
